import { useEffect, useState } from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  uploadCategoryImage,
  deleteCategoryImage,
  getImageUrl,
} from "../../utils/salatMahsoolApi";
import { canWrite, canDelete } from "./common";

const emptyForm = { name: "", name_en: "", image: "", is_active: true };

const CategoryFormDialog = ({ open, onClose, initial, onSaved }) => {
  const notify = useNotify();
  const [form, setForm] = useState(emptyForm);
  const [currentId, setCurrentId] = useState(initial?.id || null);
  const [uploading, setUploading] = useState(false);
  const [removingImage, setRemovingImage] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial ? { ...emptyForm, ...initial } : emptyForm);
    setCurrentId(initial?.id || null);
  }, [initial, open]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const updated = await uploadCategoryImage(currentId, file);
      setForm((f) => ({ ...f, image: updated.image }));
      onSaved();
    } catch (err) {
      notify(`Image upload failed: ${err.message}`, { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    setRemovingImage(true);
    try {
      const updated = await deleteCategoryImage(currentId);
      setForm((f) => ({ ...f, image: updated.image }));
      onSaved();
    } catch (err) {
      notify(`Image remove failed: ${err.message}`, { type: "error" });
    } finally {
      setRemovingImage(false);
    }
  };

  const handleSave = async () => {
    if (!form.name) {
      notify("Name is required", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: form.name,
        name_en: form.name_en || undefined,
        is_active: form.is_active,
      };
      if (currentId) {
        await updateCategory(currentId, data);
        notify("Category saved", { type: "success" });
      } else {
        const created = await createCategory(data);
        notify("Category created. You can now add an image.", { type: "success" });
        setCurrentId(created.id);
      }
      onSaved();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{currentId ? "Edit Category" : "Add Category"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            fullWidth
          />
          <TextField
            label="Name (English)"
            value={form.name_en || ""}
            onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
            fullWidth
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Image
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {form.image ? (
                <Box position="relative" display="inline-flex">
                  <img
                    src={getImageUrl(form.image)}
                    alt=""
                    style={{ height: 64, width: 64, objectFit: "cover", borderRadius: 4 }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    disabled={removingImage}
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      bgcolor: "background.paper",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "error.light" },
                    }}
                  >
                    {removingImage ? <CircularProgress size={16} /> : <CloseIcon fontSize="small" />}
                  </IconButton>
                </Box>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    height: 64,
                    width: 64,
                    borderRadius: 1,
                    border: "1px dashed",
                    borderColor: "divider",
                    color: "text.disabled",
                  }}
                >
                  <Typography variant="caption">No image</Typography>
                </Box>
              )}
              <Box>
                <Button variant="outlined" component="label" disabled={uploading || !currentId}>
                  {uploading ? "Uploading..." : form.image ? "Replace Image" : "Upload Image"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif"
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
                {!currentId && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    Save the category first to enable image upload.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">Active</Typography>
            <Switch
              checked={!!form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || uploading || removingImage}
        >
          {currentId ? "Save Changes" : "Create Category"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteConfirmDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Delete Category</DialogTitle>
    <DialogContent>
      <Typography>
        This will permanently delete this category. This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="error" variant="contained" onClick={onConfirm}>
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const SalatCategoriesPage = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canEdit = canWrite(permissions);
  const canRemove = canDelete(permissions);

  const refetch = () => {
    setLoading(true);
    listCategories()
      .then(setCategories)
      .catch((err) => notify(`Error: ${err.message}`, { type: "error" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleActive = async (category) => {
    try {
      await updateCategory(category.id, { is_active: !category.is_active });
      refetch();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteTarget.id);
      notify("Category deleted", { type: "success" });
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    }
  };

  return (
    <Card sx={{ m: 2, p: 2 }}>
      <Title title="Salat Mahsool - Categories" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Salat Mahsool Categories</Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            Add Category
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Name (EN)</TableCell>
                <TableCell>Active</TableCell>
                {(canEdit || canRemove) && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.id} hover>
                  <TableCell>
                    {cat.image ? (
                      <img
                        src={getImageUrl(cat.image)}
                        alt={cat.name}
                        style={{ height: 40, width: 40, objectFit: "cover", borderRadius: 4 }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{cat.name}</TableCell>
                  <TableCell>{cat.name_en || "-"}</TableCell>
                  <TableCell>
                    <Switch
                      checked={!!cat.is_active}
                      disabled={!canEdit}
                      onChange={() => handleToggleActive(cat)}
                    />
                  </TableCell>
                  {(canEdit || canRemove) && (
                    <TableCell align="right">
                      {canEdit && (
                        <IconButton
                          onClick={() => {
                            setEditing(cat);
                            setFormOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canRemove && (
                        <IconButton onClick={() => setDeleteTarget(cat)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No categories yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CategoryFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editing}
        onSaved={refetch}
      />
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Card>
  );
};

export default SalatCategoriesPage;
